import {KTIcon, toAbsoluteUrl} from '../../../../_metronic/helpers'
import {useGetUser} from '../../store/userStore'
import {Button, Col, Form, InputGroup, Row, Spinner} from 'react-bootstrap'
import {useFormik} from 'formik'
import * as Yup from 'yup'
import {useEffect, useState} from 'react'
import {useMutation, useQuery} from '@apollo/client'
import {CHANGE_OWNER_PASSWORD, EDIT_USER_STR} from '../../gql/mutations/userMutations'
import {IResUpdateOwnPass} from '../../types/membersPolicy'
import {toast} from 'react-toastify'
import {prependBackendUrl} from '../../gql/client'
import {GET_ONE_MEMBER_USER} from '../../gql/queries/usersQuery'
import {IResAllUsers} from '../../types/users'
import {useUploadFile} from '../../hooks/useUploadFile'
import {IResEditMember} from '../../types/users'
import {parseId} from '../../helpers'
import {TwoFactorSettingsPanel} from '../../components/security/TwoFactorSettingsPanel'
import {PasskeySettingsPanel} from '../../components/security/PasskeySettingsPanel'

export const ProfilePage = () => {
  const [gqlChangePass, {data, loading, error}] =
    useMutation<IResUpdateOwnPass>(CHANGE_OWNER_PASSWORD)
  const {user, logout, syncUserProfile} = useGetUser()

  // Obtener ID del usuario desde el store (más confiable que localStorage)
  const userId = user?.user?.id

  // Query para obtener datos frescos del usuario
  const {
    data: userData,
    loading: loadingUser,
    refetch: refetchUser,
  } = useQuery<IResAllUsers>(GET_ONE_MEMBER_USER, {
    variables: {
      id: userId ? parseInt(userId) : undefined,
      first: 1,
    },
    skip: !userId,
    fetchPolicy: 'no-cache',
  })

  const currentUser = userData?.allUsers?.edges?.[0]?.node

  const twoFactorEnabled = Boolean(
    currentUser?.twoFactorEnabled ?? user?.user?.twoFactorEnabled ?? false
  )
  const twoFactorRequired = Boolean(
    currentUser?.twoFactorRequired ?? user?.user?.twoFactorRequired ?? false
  )
  const twoFactorEmailFallbackEnabled = Boolean(
    currentUser?.twoFactorEmailFallbackEnabled ??
      user?.user?.twoFactorEmailFallbackEnabled ??
      false
  )
  const passkeyCredentials = currentUser?.passkeyCredentials || []

  // Debug: ver qué devuelve la query
  useEffect(() => {
    console.log('[ProfilePage] Query data:', {userData, currentUser, userId, loadingUser})
  }, [userData, currentUser, userId, loadingUser])

  useEffect(() => {
    if (!currentUser) return

    syncUserProfile({
      twoFactorEnabled: Boolean(currentUser.twoFactorEnabled),
      twoFactorRequired: Boolean(currentUser.twoFactorRequired),
      twoFactorEmailFallbackEnabled: Boolean(currentUser.twoFactorEmailFallbackEnabled),
    })
  }, [currentUser, syncUserProfile])

  // Upload de avatar
  const [uploadFile, {data: dataUpload, loading: loadingUpload}] = useUploadFile<IResEditMember>()
  const [avatarPreview, setAvatarPreview] = useState<string | null>(null)
  const [selectedAvatar, setSelectedAvatar] = useState<File | null>(null)

  const [avatarError, setAvatarError] = useState(false)
  const [showPass, setShowPass] = useState(false)
  const [showConfirmPass, setShowConfirmPass] = useState(false)
  const [showOldPass, setShowOldPass] = useState(false)

  const formik = useFormik({
    initialValues: {
      password: '',
      confirmPassword: '',
      oldPassword: '',
    },
    validationSchema: Yup.object({
      password: Yup.string()
        .min(6, 'Password must be at least 6 characters')
        .matches(/[A-Z]/, 'Password must contain at least one uppercase letter')
        .required('Password is require'),
      confirmPassword: Yup.string()
        .oneOf([Yup.ref('password')], 'Passwords must match')
        .required('Password is require'),
      oldPassword: Yup.string().required('Old Password is require'),
    }),
    onSubmit: (v, o) => {
      if (v.password && v.password !== v.confirmPassword) {
        o.setFieldError('confirmPassword', 'Passwords must match')
        return
      }
      gqlChangePass({
        variables: {
          claveActual: v.oldPassword,
          nuevaClave: v.password,
        },
      })
    },
  })

  useEffect(() => {
    if (error) toast.error(`Error: ${error}`)
  }, [error])

  useEffect(() => {
    setAvatarError(false)
  }, [currentUser?.avatar])

  // Manejar respuesta de upload de avatar
  useEffect(() => {
    if (!dataUpload) return
    if (dataUpload?.updateUserMutation?.user?.id) {
      toast.success('Avatar updated successfully')
      refetchUser()
      setSelectedAvatar(null)
      setAvatarPreview(null)
    } else if (dataUpload?.updateUserMutation?.errors?.length > 0) {
      toast.error(`Error: ${dataUpload?.updateUserMutation?.errors[0]?.message || 'Something wrong!'}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpload])

  // Función para subir avatar
  const handleAvatarUpload = async (file: File) => {
    console.log('[ProfilePage] handleAvatarUpload called', {file, userId, currentUser, loadingUser})

    if (!userId) {
      console.error('[ProfilePage] Missing userId')
      toast.error('User ID not found')
      return
    }

    if (loadingUser) {
      toast.info('Loading user data, please wait...')
      return
    }

    if (!currentUser) {
      console.error('[ProfilePage] User data not loaded')
      toast.error('User data not available')
      return
    }

    const formdata = new FormData()
    formdata.append('0', file, file.name)

    const map = {'0': ['variables.userData.avatar']}

    const userData = {
      firstName: currentUser.firstName,
      lastName: currentUser.lastName,
      email: currentUser.email,
      cellphone: currentUser.cellphone,
      npn: currentUser.npn,
      applyDeductions: currentUser.applyDeductions,
      isActive: currentUser.isActive,
      primaryGroup: currentUser.primaryGroup ? parseId(currentUser.primaryGroup.id) : undefined,
      avatar: null,
    }

    console.log('[ProfilePage] userData built', userData)

    const obj = {
      query: EDIT_USER_STR,
      variables: {
        userData,
        userId: userId,
      },
    }

    formdata.append('map', JSON.stringify(map))
    formdata.append('operations', JSON.stringify(obj))

    console.log('[ProfilePage] Calling uploadFile')
    uploadFile(formdata)
  }

  useEffect(() => {
    if (!data) return

    if (data.changeOwnPasswordMutation.success) {
      toast.success('Password updated successfully')
      logout()
    } else if (
      data.changeOwnPasswordMutation.errors &&
      data.changeOwnPasswordMutation.errors.length > 0
    ) {
      toast.error(
        `Error: ${data.changeOwnPasswordMutation.errors[0]?.message || 'Something wrong!'}`
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data])

  // useEffect para manejar el resultado del upload de avatar
  useEffect(() => {
    if (!dataUpload) return
    if (dataUpload?.updateUserMutation?.user?.id) {
      toast.success('Avatar updated successfully')
      refetchUser()
      setSelectedAvatar(null)
      setAvatarPreview(null)
    } else if (dataUpload?.updateUserMutation?.errors?.length > 0) {
      toast.error(`Error: ${dataUpload?.updateUserMutation?.errors[0]?.message || 'Something wrong!'}`)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataUpload])

  if (!user) return null

  // Usar datos del usuario actual (query fresca) o fallback al store
  const displayUser = currentUser || user.user
  const avatarSrc =
    avatarPreview ||
    (!avatarError && displayUser?.avatar
      ? prependBackendUrl(displayUser.avatar)
      : toAbsoluteUrl('/media/avatars/blank.png'))

  return (
    <div>
      <div className='card mb-5 mb-xl-10'>
        <div className='card-body pt-9 pb-0'>
          <div className='d-flex flex-wrap flex-sm-nowrap mb-3'>
            <div className='me-7 mb-4'>
              <div
                className='position-relative'
                style={{
                  width: '160px',
                  height: '160px',
                }}
              >
                {loadingUser && (
                  <div
                    className='d-flex align-items-center justify-content-center'
                    style={{
                      width: '100%',
                      height: '100%',
                      borderRadius: '0.95rem',
                      backgroundColor: '#f5f5f5',
                    }}
                  >
                    <Spinner animation='border' />
                  </div>
                )}
                {!loadingUser && (
                  <>
                    <img
                      src={avatarSrc}
                      alt={`${displayUser?.firstName || ''} ${displayUser?.lastName || ''}`.trim()}
                      onError={() => setAvatarError(true)}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        borderRadius: '0.95rem',
                      }}
                    />
                    <label
                      className='btn btn-icon btn-circle btn-active-color-primary w-35px h-35px bg-body shadow'
                      style={{
                        position: 'absolute',
                        bottom: '5px',
                        right: '5px',
                        cursor: 'pointer',
                      }}
                      title='Change avatar'
                    >
                      <i className='bi bi-pencil-fill fs-6'></i>
                      <input
                        type='file'
                        accept='.png, .jpg, .jpeg, .webp'
                        onChange={(e) => {
                          const file = e.target.files?.[0]
                          if (file) {
                            setSelectedAvatar(file)
                            // Preview
                            const reader = new FileReader()
                            reader.onloadend = () => {
                              setAvatarPreview(reader.result as string)
                            }
                            reader.readAsDataURL(file)
                          }
                        }}
                        style={{display: 'none'}}
                      />
                    </label>
                    {avatarPreview && (
                      <>
                        <button
                          className='btn btn-icon btn-circle btn-active-color-success w-35px h-35px bg-body shadow'
                          style={{
                            position: 'absolute',
                            bottom: '5px',
                            left: '5px',
                          }}
                          title='Save avatar'
                          onClick={() => selectedAvatar && handleAvatarUpload(selectedAvatar)}
                          disabled={loadingUpload}
                        >
                          {loadingUpload ? (
                            <Spinner animation='border' size='sm' />
                          ) : (
                            <i className='bi bi-check-lg fs-4'></i>
                          )}
                        </button>
                        <button
                          className='btn btn-icon btn-circle btn-active-color-danger w-35px h-35px bg-body shadow'
                          style={{
                            position: 'absolute',
                            top: '5px',
                            right: '5px',
                          }}
                          title='Cancel'
                          onClick={() => {
                            setSelectedAvatar(null)
                            setAvatarPreview(null)
                          }}
                        >
                          <i className='bi bi-x fs-4'></i>
                        </button>
                      </>
                    )}
                    <div className='position-absolute translate-middle bottom-0 start-100 mb-6 bg-success rounded-circle border border-4 border-white h-20px w-20px'></div>
                  </>
                )}
              </div>
            </div>

            <div className='flex-grow-1'>
              <div className='d-flex justify-content-between align-items-start flex-wrap mb-2'>
                <div className='d-flex flex-column'>
                  <div className='d-flex align-items-center mb-2'>
                    <span className='text-gray-800 text-hover-primary fs-2 fw-bolder me-1'>
                       {displayUser?.firstName || ''} {displayUser?.lastName || ''}
                    </span>
                    <span>
                      <KTIcon iconName='verify' className='fs-1 text-primary' />
                    </span>
                  </div>

                  <div className='d-flex flex-wrap fw-bold fs-6 mb-4 pe-2'>
                    <a
                      href='/#'
                      className='d-flex align-items-center text-gray-400 text-hover-primary me-5 mb-2'
                    >
                      {currentUser?.primaryGroup?.name ? (
                          <>
                            <KTIcon iconName='profile-circle' className='fs-4 me-1' />
                            <span className='fw-bold'>Role: </span>
                            {currentUser.primaryGroup.name}
                          </>
                        ) : user?.groups?.length ? (
                          <>
                            <KTIcon iconName='profile-circle' className='fs-4 me-1' />
                            <span className='fw-bold'>Role: </span>
                            {user.groups[0]}
                          </>
                        ) : null}
                    </a>


                    <a
                      href='/#'
                      className='d-flex align-items-center text-gray-400 text-hover-primary mb-2'
                    >
                      <KTIcon iconName='sms' className='fs-4 me-1' />
                      {currentUser?.email || user.payload?.email || 'N/R'}
                    </a>
                  </div>
                </div>

                <div className='d-flex my-4'></div>
              </div>

              <div className='d-flex flex-wrap flex-stack'>
                <div className='d-flex flex-column flex-grow-1 pe-8'>
                  <div className='d-flex flex-wrap'>
                    {/* <InfoBalance id={`${parseId(node.id)}`} flagBalance={flagBalance} /> */}

                    {/* <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                      <div className='d-flex align-items-center'>
                        <KTIcon iconName='arrow-down' className='fs-3 text-danger me-2' />
                        <div className='fs-2 fw-bolder'>75</div>
                      </div>

                      <div className='fw-bold fs-6 text-gray-400'>Projects</div>
                    </div>

                    <div className='border border-gray-300 border-dashed rounded min-w-125px py-3 px-4 me-6 mb-3'>
                      <div className='d-flex align-items-center'>
                        <KTIcon iconName='arrow-up' className='fs-3 text-success me-2' />
                        <div className='fs-2 fw-bolder'>60%</div>
                      </div>

                      <div className='fw-bold fs-6 text-gray-400'>Success Rate</div>
                    </div> */}
                  </div>
                </div>
                {/* strat*/}{' '}
                {/* <div className='d-flex align-items-center w-200px w-sm-300px flex-column mt-3'>
                  <div className='d-flex justify-content-between w-100 mt-auto mb-2'>
                    <span className='fw-bold fs-6 text-gray-400'>Profile Compleation</span>
                    <span className='fw-bolder fs-6'>50%</span>
                  </div>
                  <div className='h-5px mx-3 w-100 bg-light mb-3'>
                    <div
                      className='bg-success rounded h-5px'
                      role='progressbar'
                      style={{width: '50%'}}
                    ></div>
                  </div>
                </div>{' '} */}
                {/* End*/}
              </div>
            </div>
          </div>

          <div className='d-flex overflow-auto h-55px'>
            <ul className='nav nav-stretch nav-line-tabs nav-line-tabs-2x border-transparent fs-5 fw-bolder flex-nowrap'>
              <li className='nav-item'>
                <button className={`nav-link text-active-primary me-6 active`}>Settings</button>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className='card mb-5 mb-xl-10' id='kt_profile_details_view'>
        <div className='card-header cursor-pointer'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>Change Password</h3>
          </div>
        </div>

        <form onSubmit={formik.handleSubmit}>
          <div className='card-body border-top p-9'>
            <Row>
              <Col lg={12} md={12} sm={12}>
                <Form.Group>
                  <Form.Label>Old password</Form.Label>
                  <InputGroup className='mb-3'>
                    <Form.Control
                      type={showOldPass ? 'text' : 'password'}
                      placeholder='Insert old password'
                      aria-label='Insert oldPassword'
                      aria-describedby='oldPassword'
                      name='oldPassword'
                      value={formik.values.oldPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={Boolean(formik.errors.oldPassword && formik.touched.oldPassword)}
                    />
                    <Button
                      variant='outline-secondary'
                      id='button-addon3'
                      style={{border: '1px solid #dbdfe9'}}
                      onClick={() => setShowOldPass((p) => !p)}
                    >
                      <i
                        className={`bi bi-${showPass ? 'eye-slash' : 'eye'} fs-2`}
                        style={{color: 'inherit'}}
                      />
                    </Button>
                  </InputGroup>
                  {formik.errors.oldPassword && formik.touched.oldPassword && (
                    <small className='text-danger'>{formik.errors.oldPassword}</small>
                  )}
                </Form.Group>
              </Col>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>Password</Form.Label>
                  <InputGroup className='mb-3'>
                    <Form.Control
                      type={showPass ? 'text' : 'password'}
                      placeholder='Insert password'
                      aria-label='Insert password'
                      aria-describedby='password'
                      name='password'
                      value={formik.values.password}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={Boolean(formik.errors.password && formik.touched.password)}
                    />
                    <Button
                      variant='outline-secondary'
                      id='button-addon2'
                      style={{border: '1px solid #dbdfe9'}}
                      onClick={() => setShowPass((p) => !p)}
                    >
                      <i
                        className={`bi bi-${showPass ? 'eye-slash' : 'eye'} fs-2`}
                        style={{color: 'inherit'}}
                      />
                    </Button>
                  </InputGroup>
                  {formik.errors.password && formik.touched.password && (
                    <small className='text-danger'>{formik.errors.password}</small>
                  )}
                </Form.Group>
              </Col>
              <Col md={6} sm={12}>
                <Form.Group>
                  <Form.Label>Confirm Password</Form.Label>
                  <InputGroup className='mb-3'>
                    <Form.Control
                      type={showConfirmPass ? 'text' : 'password'}
                      placeholder='Confirm password'
                      aria-label='Confirm password'
                      aria-describedby='Confirm password'
                      name='confirmPassword'
                      value={formik.values.confirmPassword}
                      onChange={formik.handleChange}
                      onBlur={formik.handleBlur}
                      isInvalid={Boolean(
                        formik.errors.confirmPassword && formik.touched.confirmPassword
                      )}
                    />
                    <Button
                      variant='outline-secondary'
                      id='Confirm-pass'
                      onClick={() => setShowConfirmPass((p) => !p)}
                      style={{border: '1px solid #dbdfe9'}}
                    >
                      <i
                        className={`bi bi-${showConfirmPass ? 'eye-slash' : 'eye'} fs-2`}
                        style={{color: 'inherit'}}
                      />
                    </Button>
                  </InputGroup>
                  {formik.errors.confirmPassword && formik.touched.confirmPassword && (
                    <small className='text-danger'>{formik.errors.confirmPassword}</small>
                  )}
                </Form.Group>
              </Col>
            </Row>
          </div>
          <div className='card-footer d-flex justify-content-end py-6 px-9'>
            <button type='submit' className='btn btn-info'>
              {!loading && 'Save Changes'}
              {loading && (
                <span className='indicator-progress' style={{display: 'block'}}>
                  Please wait...{' '}
                  <span className='spinner-border spinner-border-sm align-middle ms-2'></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>

      <div className='card mb-5 mb-xl-10'>
        <div className='card-header cursor-pointer'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>Two-Factor Authentication</h3>
          </div>
        </div>

        <div className='card-body border-top p-9'>
          <TwoFactorSettingsPanel
            canManageOwnTwoFactor
            twoFactorEnabled={twoFactorEnabled}
            twoFactorRequired={twoFactorRequired}
            twoFactorEmailFallbackEnabled={twoFactorEmailFallbackEnabled}
            onUpdated={refetchUser}
          />
        </div>
      </div>

      <div className='card mb-5 mb-xl-10'>
        <div className='card-header cursor-pointer'>
          <div className='card-title m-0'>
            <h3 className='fw-bolder m-0'>Passkeys</h3>
          </div>
        </div>

        <div className='card-body border-top p-9'>
          <PasskeySettingsPanel
            credentials={passkeyCredentials}
            canManageOwnPasskeys
            onUpdated={refetchUser}
          />
        </div>
      </div>
    </div>
  )
}
